/**
 * Checkout API — thin wrapper over `@wg/api/checkout`. Endpoint paths and
 * request/response shapes live in the shared module; this file only injects
 * the embed's instance-scoped clients:
 *
 *   - submitMockCheckout → unauthClient (endpoint authenticates via the
 *     instance public_token in the body, not a user JWT)
 *   - initiateBookingForSession → caller-supplied authed client (the
 *     session-bound httpClient), signature preserved from the original
 *     local implementation.
 */
import {
  submitMockCheckout as sharedSubmitMockCheckout,
  initiateBookingForSession as sharedInitiateBookingForSession,
  getBookingStatusForSession as sharedGetBookingStatusForSession,
  reportLikelyBookedForSession as sharedReportLikelyBookedForSession,
  type TrackingSessionSnapshot,
  type EmbedBookingInitBody,
  type MockCheckoutContact,
  type MockCheckoutGuestRow,
  type MockCheckoutLineItem,
  type MockCheckoutRequest,
  type MockCheckoutResponse,
} from '@wg/api/checkout';
import type { BookingInitResponse } from '@wg/types';
import { unauthClient } from '../client';
import { getEffectiveCampaign } from '../utils/campaign';

export type {
  TrackingSessionSnapshot,
  EmbedBookingInitBody,
  MockCheckoutContact,
  MockCheckoutGuestRow,
  MockCheckoutLineItem,
  MockCheckoutRequest,
  MockCheckoutResponse,
};

export async function submitMockCheckout(
  body: MockCheckoutRequest,
): Promise<MockCheckoutResponse> {
  return sharedSubmitMockCheckout(unauthClient, body);
}

export async function initiateBookingForSession(
  sessionId: string,
  itemId: string,
  body: EmbedBookingInitBody,
  client: import('axios').AxiosInstance,
): Promise<BookingInitResponse> {
  // Every embed booking carries the surface's effective campaign so the
  // booking sync can attribute it (first-touch claim still wins server-side).
  const campaign_code =
    body.campaign_code ?? getEffectiveCampaign() ?? undefined;
  return sharedInitiateBookingForSession(client, sessionId, itemId, {
    ...body,
    ...(campaign_code ? { campaign_code } : {}),
  });
}

export async function getBookingStatusForSession(
  sessionId: string,
  trackingSessionId: string,
  client: import('axios').AxiosInstance,
): Promise<TrackingSessionSnapshot | null> {
  return sharedGetBookingStatusForSession(client, sessionId, trackingSessionId);
}

export async function reportLikelyBookedForSession(
  sessionId: string,
  trackingSessionId: string,
  client: import('axios').AxiosInstance,
): Promise<TrackingSessionSnapshot | null> {
  return sharedReportLikelyBookedForSession(
    client,
    sessionId,
    trackingSessionId,
  );
}
